import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  EVENT_TYPE_NAMES,
  liveFrameSchema,
  liveClientMessageSchema,
  liveNonActivityFrameSchema,
  ulidSchema,
  type LiveClientMessage,
} from '@molt/shared';
import type { BoardSubscriptionStatus, LiveSocketFrame } from './socket';
import { queryKeys } from './query-keys';

const CLOSE_POLICY_VIOLATION = 1008;
const CLOSE_NORMAL = 1000;
const RECONNECT_DELAY_MS = 750;

export type CardLiveStatus = BoardSubscriptionStatus | 'disabled';
export type CardLiveSendMessage = (message: LiveClientMessage) => void;

export interface UseCardLiveOptions {
  readonly boardId: string | undefined;
  readonly cardId: string | undefined;
  readonly enabled: boolean;
  readonly onFrame?: (frame: LiveSocketFrame) => void;
  readonly onSendReady?: ((send: CardLiveSendMessage | null) => void) | undefined;
}

export function cardSocketUrl(boardId: string, cardId: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const params = new URLSearchParams({ boardId, cardId });
  return `${protocol}//${window.location.host}/ws?${params.toString()}`;
}

function parseCardFrame(data: unknown): LiveSocketFrame | null {
  if (typeof data !== 'string') return null;

  let json: unknown;
  try {
    json = JSON.parse(data);
  } catch {
    return null;
  }

  if (typeof json !== 'object' || json === null) return null;

  const type: unknown = (json as { type?: unknown }).type;
  if (typeof type !== 'string') return null;

  const isActivityEntry = (EVENT_TYPE_NAMES as readonly string[]).includes(type);
  const parsed = isActivityEntry
    ? liveFrameSchema.safeParse(json)
    : liveNonActivityFrameSchema.safeParse(json);

  return parsed.success ? parsed.data : null;
}

export function useCardLive({
  boardId,
  cardId,
  enabled,
  onFrame,
  onSendReady,
}: UseCardLiveOptions): CardLiveStatus {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<CardLiveStatus>('disabled');
  const onFrameRef = useRef(onFrame);
  const onSendReadyRef = useRef(onSendReady);

  useEffect(() => {
    onFrameRef.current = onFrame;
  }, [onFrame]);

  useEffect(() => {
    onSendReadyRef.current = onSendReady;
  }, [onSendReady]);

  useEffect(() => {
    if (
      !enabled ||
      boardId === undefined ||
      cardId === undefined ||
      !ulidSchema.safeParse(boardId).success ||
      !ulidSchema.safeParse(cardId).success ||
      typeof WebSocket === 'undefined'
    ) {
      setStatus('disabled');
      onSendReadyRef.current?.(null);
      return undefined;
    }

    let disposed = false;
    let everOpened = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let socket: WebSocket | null = null;

    function invalidateCardBack(): void {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId!) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.fieldVersions(cardId!),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.activity(cardId!, 'all'),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.activity(cardId!, 'comments'),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards.comments(cardId!) });
    }

    function open(): void {
      setStatus(everOpened ? 'reconnecting' : 'connecting');
      socket = new WebSocket(cardSocketUrl(boardId!, cardId!));

      socket.addEventListener('open', () => {
        if (disposed) return;
        const closingGap = everOpened;
        everOpened = true;
        setStatus('open');
        onSendReadyRef.current?.((message) => {
          const parsed = liveClientMessageSchema.parse(message);
          socket?.send(JSON.stringify(parsed));
        });
        if (closingGap) invalidateCardBack();
      });

      socket.addEventListener('message', (event: MessageEvent) => {
        if (disposed) return;
        const frame = parseCardFrame(event.data);
        if (frame === null) return;
        if (frame.type !== 'comment.typing') invalidateCardBack();
        onFrameRef.current?.(frame);
      });

      socket.addEventListener('close', (event: CloseEvent) => {
        if (disposed) return;
        socket = null;
        onSendReadyRef.current?.(null);
        if (event.code === CLOSE_POLICY_VIOLATION) {
          setStatus('refused');
          return;
        }
        setStatus(everOpened ? 'reconnecting' : 'closed');
        reconnectTimer = setTimeout(open, RECONNECT_DELAY_MS);
      });

      socket.addEventListener('error', () => {
        // The browser follows with close; reconnecting is driven there.
      });
    }

    open();

    return () => {
      disposed = true;
      onSendReadyRef.current?.(null);
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      socket?.close(CLOSE_NORMAL);
      socket = null;
    };
  }, [boardId, cardId, enabled, queryClient]);

  return enabled && boardId !== undefined && cardId !== undefined ? status : 'disabled';
}
